import type { FC } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { PublicStorageLocationsRequest } from 'src/apis/storage/use-public-storage-locations';
import type { PublicStorageOffersRequest } from 'src/apis/storage/use-public-storage-offers';
import { useStorageOfferCreate } from 'src/apis/storage/use-storage-offer-create';
import type { StorageOfferDetailsRequest } from 'src/apis/storage/use-storage-offer-details';
import type { StorageServiceRootRequest } from 'src/apis/storage/use-storage-service-root';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { Form } from 'src/components/form/form';
import { PageHeader } from 'src/components/layout/page-header';
import type { Successful } from 'src/hooks/use-axios';
import { useNow } from 'src/hooks/use-now';
import { useReferrer } from 'src/hooks/use-referrer';
import { useTitle } from 'src/hooks/use-title';
import { StorageOfferFields } from 'src/pages/storage/offers-new/create/fields';
import {
  formValuesToWriteData,
  getStorageOfferSchema,
} from 'src/pages/storage/offers-new/create/schema';
import type { StorageOfferFormValues } from 'src/pages/storage/offers-new/create/schema';
import {
  convertTimeInMinutesToHoursMinutes,
  mapProductComponentsFromDetails,
  mapTypeOfProductToForm,
} from 'src/pages/storage/offers-new/utils';
import { Forbidden } from 'src/utils/errors';

type Props = {
  storageServiceRoot: Successful<StorageServiceRootRequest>;
  storageLocations: Successful<PublicStorageLocationsRequest>;
  storageOffers: Successful<PublicStorageOffersRequest>;
  storageOfferDetails: Successful<StorageOfferDetailsRequest>;
};

export const StorageOfferCopyForm: FC<Props> = ({
  storageServiceRoot,
  storageLocations,
  storageOffers,
  storageOfferDetails,
}) => {
  const details = storageOfferDetails.response.data;

  if (!details.ownOffer) {
    throw new Forbidden();
  }

  const createOfferLink = storageOffers.response.data._links.createStorageOffer;
  const prepareFileUploadLink =
    storageServiceRoot.response.data._links.prepareFileUpload;

  if (!createOfferLink || !prepareFileUploadLink) {
    throw new Forbidden();
  }

  const { productType, typeOfProductUnit } = mapTypeOfProductToForm(
    details.typeOfProduct
  );
  const biddingWindowDuration =
    details.biddingWindowDurationInMinutes != null
      ? convertTimeInMinutesToHoursMinutes(
          details.biddingWindowDurationInMinutes
        )
      : null;
  const timeBetweenBiddingWindows =
    details.timeBetweenBiddingWindowsInMinutes != null
      ? convertTimeInMinutesToHoursMinutes(
          details.timeBetweenBiddingWindowsInMinutes
        )
      : null;

  const productComponents = mapProductComponentsFromDetails(
    details.productComponents
  ).map((productComponent) => ({
    ...productComponent,
    runtimeStart: null,
    runtimeEnd: null,
  }));
  const attachedFiles =
    details.attachedFiles.length > 0
      ? details.attachedFiles.map((f) => ({
          file: { id: f.id, fileName: f.fileName },
        }))
      : [{ file: null }];

  const title = `Copy of ${details.offerName}`;
  useTitle(title);

  const referrer = useReferrer({
    label: 'Storage Offers',
    location: '/storage/offers-new',
  });

  const createOffer = useStorageOfferCreate();
  const navigate = useNavigate();
  const now = useNow();

  useEffect(() => {
    if (!createOffer.response) {
      return;
    }
    toast.success('Storage offer was successfully created.');
    navigate('/storage/offers-new');
    // oxlint-disable-next-line exhaustive-deps
  }, [createOffer.response]);

  return (
    <div className="grid items-center gap-xs">
      <PageHeader title={title} referrer={referrer} />

      <Card data-testid="copy-storage-offer-form">
        <Form
          initialValues={{
            name: `${details.offerName}-Copy`,
            storageLocationId: {
              label: details.locationName,
              value: details.locationId,
            },
            productPeriod: null,
            productRuntimeStart: null,
            productRuntimeEnd: null,
            publication: null,
            biddingWindowStart: null,
            biddingWindowEnd: null,
            firstRoundBiddingWindowStart: null,
            biddingWindowDurationHours: biddingWindowDuration?.hours ?? null,
            biddingWindowDurationMinutes:
              biddingWindowDuration?.minutes ?? null,
            timeBetweenBiddingWindowsHour:
              timeBetweenBiddingWindows?.hours ?? null,
            timeBetweenBiddingWindowsMinute:
              timeBetweenBiddingWindows?.minutes ?? null,
            productType,
            typeOfProductUnit,
            allocationMethod: details.allocationMethod,
            productName: details.productName,
            availableQuantity: details.availableQuantity,
            priceTimeUnit: details.priceTimeUnit,
            reservePrice: details.reservePrice?.amount ?? null,
            priceQuantityUnit: details.priceQuantityUnit,
            productComponents,
            offerSpecificNote: details.offerSpecificNote ?? null,
            attachedFiles,
          }}
          schema={(values: StorageOfferFormValues) =>
            getStorageOfferSchema({
              now,
              publication: values.publication,
              biddingWindowStart: values.biddingWindowStart,
              biddingWindowEnd: values.biddingWindowEnd,
              productRuntimeStart: values.productRuntimeStart,
              productRuntimeEnd: values.productRuntimeEnd,
              productPeriod: values.productPeriod,
              allocationMethod: values.allocationMethod,
              biddingWindowDurationHours: values.biddingWindowDurationHours,
              biddingWindowDurationMinutes: values.biddingWindowDurationMinutes,
              timeBetweenBiddingWindowsHour:
                values.timeBetweenBiddingWindowsHour,
              timeBetweenBiddingWindowsMinute:
                values.timeBetweenBiddingWindowsMinute,
            })
          }
          onSubmit={(values) => {
            const data = formValuesToWriteData(values);
            createOffer.execute({
              url: createOfferLink.href,
              data,
            });
          }}
          constraintViolation={createOffer.error}
          cancelOrResetButton={referrer}
          submitButton={
            <Button
              type="submit"
              disabled={createOffer.pending}
              data-testid="create-storage-offer"
            >
              {createOffer.pending ? 'Pending...' : 'Create Offer'}
            </Button>
          }
        >
          <StorageOfferFields
            storageLocations={storageLocations}
            prepareFileUploadLink={prepareFileUploadLink}
          />
        </Form>
      </Card>
    </div>
  );
};
